import type { GetQuestionDetailResponse } from '@compass/client/types/analytics';
import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import { analyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';
import { serializeMultiValueParam } from '@/utils/urlParams';

import { analyticsQueryKeys } from './useOrgAnalytics';

interface UseQuestionDetailArgs {
  organizationId: string | null;
  metricKey: string | null;
  programIds?: string[];
  agentIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
  // topAgents caps the per-agent breakdown; the page requests 10 for display and
  // a large value for the "export all" download.
  topAgents?: number;
}

// buildParams mirrors the analytics query-param convention: repeated filters are
// sent comma-joined (the backend splits on ","), and dates are converted to
// timezone-correct UTC. Passed via options.params (not the request body) so the
// generated service's array-typed request fields don't force array serialization.
const buildParams = (args: UseQuestionDetailArgs): Record<string, string | number | undefined> => ({
  program_ids: args.programIds && args.programIds.length ? args.programIds.join(',') : undefined,
  agent_ids: args.agentIds && args.agentIds.length ? args.agentIds.join(',') : undefined,
  call_directions: serializeMultiValueParam(args.callDirections),
  start: args.start ? naiveDateToUtc(args.start, args.timeZone, 'start') : undefined,
  end: args.end ? naiveDateToUtc(args.end, args.timeZone, 'end') : undefined,
  top_agents: args.topAgents,
});

export const useQuestionDetail = (args: UseQuestionDetailArgs) => {
  const { organizationId, metricKey } = args;
  const params = buildParams(args);

  return useQuery({
    queryKey: [...analyticsQueryKeys.all, 'questions', 'detail', metricKey, params] as const,
    queryFn: (): Promise<GetQuestionDetailResponse> =>
      analyticsService.getQuestionDetail(
        { organization_id: organizationId ?? '', metric_key: metricKey ?? '' },
        { params },
      ),
    enabled: !!organizationId && !!metricKey,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};

// fetchAllQuestionAgents fetches the full per-agent breakdown (not just the
// display top-N) for the CSV export. It reuses the same query key space so a
// warm cache is shared where possible.
export const buildQuestionAgentsExportRequest = (args: UseQuestionDetailArgs) => ({
  organizationId: args.organizationId,
  metricKey: args.metricKey,
  params: buildParams({ ...args, topAgents: 100000 }),
});
